import type { ItemId, Recipe } from "@craftoff/game-core";
import generated from "./minecraft-data.generated.json";

export const MINECRAFT_VERSION = generated.minecraftVersion;
export const ITEMS = generated.items as Record<ItemId, { name: string; tone: string }>;
export const RECIPES = generated.recipes as Recipe[];

function isLearnOnlyItem(itemId: ItemId): boolean {
  return itemId.startsWith("waxed_");
}

export const DECOY_ITEMS = (Object.keys(ITEMS) as ItemId[]).filter((itemId) => !isLearnOnlyItem(itemId));

const MATCH_VARIANT_SUFFIXES = [
  "stained_glass_pane",
  "concrete_powder",
  "glazed_terracotta",
  "smithing_template",
  "stripped_hyphae",
  "stripped_stem",
  "stripped_wood",
  "stripped_log",
  "hanging_sign",
  "pressure_plate",
  "banner_pattern",
  "dead_coral_block",
  "dead_coral_fan",
  "coral_block",
  "coral_fan",
  "chest_boat",
  "chest_raft",
  "fence_gate",
  "horse_armor",
  "music_disc",
  "shulker_box",
  "stained_glass"
] as const;

function variantKind(itemId: ItemId): string | null {
  if (/^raw_(copper|gold|iron)$/.test(itemId)) return "raw_material";
  return MATCH_VARIANT_SUFFIXES.find((suffix) => itemId === suffix || itemId.endsWith(`_${suffix}`))
    ?? itemId.split("_").at(-1)
    ?? null;
}

const variantKindCounts = new Map<string, number>();
for (const itemId of DECOY_ITEMS) {
  const kind = variantKind(itemId);
  if (kind) variantKindCounts.set(kind, (variantKindCounts.get(kind) ?? 0) + 1);
}

export function getMatchItemFamily(itemId: ItemId): string {
  const kind = variantKind(itemId);
  return kind && (variantKindCounts.get(kind) ?? 0) > 1 ? `variant:${kind}` : `item:${itemId}`;
}

export const MATCH_RECIPES = RECIPES.filter((recipe) => {
  const ingredientCount = recipe.grid.filter((item) => item !== null).length;
  if (ingredientCount < 2) return false;
  const ingredientIds = [...new Set(recipe.grid.filter((item): item is ItemId => item !== null))];
  if (isLearnOnlyItem(recipe.outputItemId) || ingredientIds.some(isLearnOnlyItem)) return false;
  return new Set(ingredientIds.map(getMatchItemFamily)).size === ingredientIds.length;
});

export function getRecipe(id: string): Recipe | undefined {
  return RECIPES.find((recipe) => recipe.id === id);
}
